import { cn, NoddiIcon, type NoddiIconName } from 'noddi-ui-common';
import { ReactNode, useId, useRef } from 'react';
import { Button } from '../../../base/button/button';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../../../base/command';
import { useSearchPopover } from '../../../hooks/useSearchPopover';
import { NoddiSkeleton } from '../../Feedback/NoddiSkeleton';
import { NoddiSpinner } from '../../Feedback/NoddiSpinner';
import { NoddiSeparator } from '../../Other/NoddiSeparator';
import { Popover, PopoverContent, PopoverTrigger } from '../../Overlay/NoddiPopover';
import { NoddiTooltip } from '../../Overlay/NoddiTooltip';
import { getSelectOptionValue } from './selectOptionValue';

export interface SelectOption<T = string> {
  value: T;
  label: string;
  /** Extra text matched by local search without displaying it in the trigger. */
  searchText?: string;
  disabled?: boolean;
  /** Optional group heading. When any option sets it, options render under grouped headings. */
  group?: string;
  /** Optional image URL (e.g. org/department logo) shown beside the label. */
  imageUrl?: string;
  /** Optional icon shown beside the label. */
  icon?: NoddiIconName;
}

export interface CommonSelectProps<T = string> {
  options: SelectOption<T>[] | undefined;
  id?: string;
  name?: string;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  onOpenChange?: (open: boolean) => void;
  disabled?: boolean;
  className?: string;
  /**
   * Classes on the outer wrapper, which holds the label and the trigger. Its default 280px minimum
   * suits a form column; `min-w-0` here and in `className` (the trigger has the same minimum) lets
   * the select shrink inside a narrower one, like a row in a phone's sheet.
   */
  wrapperClassName?: string;
  label?: string;
  tooltip?: string;
  isLoading?: boolean;
  searchable?: boolean;
  /** Show the inline clear (X) button when a value is selected. Default true. */
  clearable?: boolean;
  renderOption?: (option: SelectOption<T>) => ReactNode;
  /**
   * Replaces the default search-and-list popover content, for a select whose options need more
   * structure than one flat list (tabs, sections). Selection still goes through `onValueChange`,
   * so the trigger, the clear button and the toggle-to-clear behavior stay the select's.
   */
  renderPopoverContent?: (content: {
    searchTerm: string;
    onSearchChange: (term: string) => void;
    onValueChange: (value: string) => void;
    isLoading: boolean;
  }) => ReactNode;
  startIcon?: NoddiIconName;
  popoverClassName?: string;
  /**
   * On phones, always open the popover below the trigger and scroll the trigger to the top of
   * the screen, so the keyboard does not flip the search input off screen. Only for a field
   * high on its page (address search): low on a short page it cannot scroll up, and the
   * keyboard then covers the list.
   */
  keepPopoverBelowOnPhones?: boolean;
  // Our address search component doesn't show options unless the user has searched, so we need this prop to avoid showing a "no options" message when no search has been performed.
  noOptionsUnlessSearched?: boolean;
  translation?: {
    placeholder: string;
    searchPlaceholder: string;
    emptyMessage: string;
  };
}

interface SelectBaseProps<T = string> extends CommonSelectProps<T> {
  open: boolean;
  setOpen: (open: boolean) => void;
  currentValue: T | undefined;
  onValueChange: (newValue: string) => void;
  searchTerm: string;
  onSearchChange: (searchTerm: string) => void;
  isLocalFiltering?: boolean;
}

export function SelectBase<T = string>({
  options = [],
  id,
  name,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  placeholder = 'Select option',
  searchPlaceholder = 'Search...',
  emptyMessage = 'No option found',
  onOpenChange,
  disabled = false,
  className,
  wrapperClassName,
  label,
  tooltip,
  isLoading,
  searchable = true,
  clearable = true,
  renderOption,
  renderPopoverContent,
  startIcon,
  open,
  setOpen,
  currentValue,
  onValueChange,
  searchTerm,
  onSearchChange,
  isLocalFiltering = true,
  popoverClassName,
  keepPopoverBelowOnPhones = false,
  noOptionsUnlessSearched = false,
  translation
}: SelectBaseProps<T>) {
  const generatedId = useId();
  const controlId = id ?? name ?? generatedId;
  const labelIdentifier = `${controlId}-label`;
  const valueIdentifier = `${controlId}-value`;
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchPopover = useSearchPopover(triggerRef, open, searchable && keepPopoverBelowOnPhones);

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    onOpenChange?.(newOpen);
  };

  const safeEmptyMessage = translation?.emptyMessage ?? emptyMessage;
  const safeSearchPlaceholder = translation?.searchPlaceholder ?? searchPlaceholder;
  const safePlaceholder = translation?.placeholder ?? placeholder;

  const selectedOption = options.find((option) => option.value === currentValue);

  const defaultOptionContent = (option: SelectOption<T>) => (
    <span className='flex min-w-0 items-center gap-2'>
      {option.imageUrl ? (
        <img src={option.imageUrl} alt='' className='size-4 shrink-0 object-contain' aria-hidden='true' />
      ) : option.icon ? (
        <NoddiIcon name={option.icon} size='small' />
      ) : null}
      <span className='truncate'>{option.label}</span>
    </span>
  );

  const renderItem = (option: SelectOption<T>) => (
    <CommandItem
      key={String(option.value)}
      value={getSelectOptionValue(option)}
      onSelect={onValueChange}
      disabled={option.disabled}
      // The selected value is already in the trigger's accessible name, so the option rows do not
      // need to carry it. cmdk owns role/aria-selected on its items and overwrites anything set here.
      aria-label={`${option.label}${currentValue === option.value ? ', selected' : ''}${
        option.disabled ? ', disabled' : ''
      }`}
    >
      <div className='flex w-full items-start justify-between gap-2'>
        <div className='min-w-0 flex-1'>{renderOption ? renderOption(option) : defaultOptionContent(option)}</div>
        <NoddiIcon
          className={cn('mt-0.5 shrink-0', currentValue === option.value ? 'opacity-100' : 'opacity-0')}
          name='Check'
        />
      </div>
    </CommandItem>
  );

  const groupHeadings = options.some((option) => option.group)
    ? options.reduce<string[]>((headings, option) => {
        const heading = option.group ?? '';
        return headings.includes(heading) ? headings : [...headings, heading];
      }, [])
    : null;

  const handleClear = (event: React.MouseEvent | React.KeyboardEvent) => {
    event.stopPropagation();
    // Simulate selecting the currently selected option to trigger toggle behavior
    if (selectedOption) {
      onValueChange(getSelectOptionValue(selectedOption));
    }
  };

  return (
    <div className={cn('relative flex min-w-70 flex-col gap-0.5', wrapperClassName)}>
      {label && (
        <div className='flex items-center gap-2'>
          <label id={labelIdentifier} htmlFor={controlId} className='text-sm'>
            {label}
          </label>
          {tooltip && <NoddiTooltip content={tooltip} label={`More information about ${label}`} />}
        </div>
      )}
      <Popover open={open} onOpenChange={handleOpenChange} modal>
        <PopoverTrigger asChild>
          <Button
            ref={triggerRef}
            type='button'
            variant='outline'
            id={controlId}
            name={name}
            aria-labelledby={label ? `${labelIdentifier} ${valueIdentifier}` : valueIdentifier}
            aria-describedby={ariaDescribedBy}
            aria-invalid={ariaInvalid}
            // oxlint-disable-next-line jsx-a11y/role-has-required-aria-props -- cmdk assigns the listbox id itself, so an aria-controls here would dangle
            role='combobox'
            aria-haspopup='listbox'
            aria-expanded={open}
            disabled={disabled}
            className={cn(
              'relative h-9 min-w-70 justify-between text-left text-base font-normal md:text-sm',
              searchPopover.triggerClassName,
              !selectedOption && 'text-muted-foreground',
              className
            )}
          >
            <div className='flex min-w-0 items-center truncate'>
              {startIcon && <NoddiIcon className='mr-2' name={startIcon} />}
              {selectedOption ? (
                <span id={valueIdentifier}>{defaultOptionContent(selectedOption)}</span>
              ) : (
                <span id={valueIdentifier} className='truncate'>
                  {safePlaceholder}
                </span>
              )}
            </div>
            <div className='flex items-center'>
              {clearable && selectedOption && !disabled && (
                <>
                  {/* Same inline clear cluster as the multi-select trigger. Not a real <button>
                      (invalid inside the trigger button), so role + keyboard handling by hand. */}
                  <div
                    role='button'
                    tabIndex={0}
                    onClick={handleClear}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        handleClear(event);
                      }
                    }}
                    aria-label={label ? `Clear ${label}` : 'Clear selection'}
                    className='mx-2 flex cursor-pointer items-center justify-center rounded-xs text-muted-foreground hover:text-foreground focus-visible:ring-3 focus-visible:ring-ring focus-visible:outline-hidden'
                  >
                    <NoddiIcon name='X' />
                  </div>
                  <NoddiSeparator orientation='vertical' className='flex h-full min-h-6' />
                </>
              )}
              <div className='ml-2 size-4'>
                {isLoading ? <NoddiSpinner size='small' /> : <NoddiIcon className='opacity-50' name='ChevronsUpDown' />}
              </div>
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent
          // The list follows the trigger's width, so a long option reads in full instead of being
          // clipped by a popover narrower than the field it belongs to.
          className={cn('min-w-(--radix-popover-trigger-width) p-0', searchPopover.contentClassName, popoverClassName)}
          {...searchPopover.contentProps}
        >
          {renderPopoverContent?.({
            searchTerm,
            onSearchChange,
            onValueChange,
            isLoading: isLoading ?? false
          }) ?? (
            <Command shouldFilter={isLocalFiltering}>
              {searchable && (
                <CommandInput
                  aria-label={label ? `Search ${label}` : safeSearchPlaceholder}
                  placeholder={safeSearchPlaceholder}
                  className='h-9'
                  value={searchTerm}
                  onValueChange={onSearchChange}
                />
              )}
              <CommandList>
                {groupHeadings ? (
                  groupHeadings.map((heading) => (
                    <CommandGroup key={heading} heading={heading}>
                      {options.filter((option) => (option.group ?? '') === heading).map(renderItem)}
                    </CommandGroup>
                  ))
                ) : (
                  <CommandGroup>{options.map(renderItem)}</CommandGroup>
                )}

                {isLoading ? (
                  <CommandGroup className='-mt-2 mb-1 h-28'>
                    {Array.from({ length: 3 }).map((_, index) => (
                      <div key={index} className='flex items-center rounded-xs p-1'>
                        <NoddiSkeleton className='h-7 flex-1' />
                      </div>
                    ))}
                  </CommandGroup>
                ) : (
                  (isLocalFiltering || options.length === 0) && (
                    <CommandEmpty className='py-0'>
                      <div className='-mt-2 mb-1 flex h-28 items-center justify-center text-center'>
                        {noOptionsUnlessSearched
                          ? searchTerm.trim().length > 0
                            ? safeEmptyMessage
                            : safeSearchPlaceholder
                          : emptyMessage}
                      </div>
                    </CommandEmpty>
                  )
                )}
              </CommandList>
            </Command>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
